import { useEffect, useRef } from 'react'
import { useInView } from '@core/viewport'

import { sendEvent } from '../helpers/sendEvent'
import { type TAnalyticEvent } from '../types/analyticEventTypes'

type TUsePromoAnalyticsParams = {
    viewEvent: TAnalyticEvent
    clickEvent: TAnalyticEvent
}

/**
 * View/click analytics for promo/banner widgets. Fires `viewEvent` once when the element
 * scrolls into view (attach the returned `ref`), and returns `handleClick` for `clickEvent`.
 */
export const usePromoAnalytics = <T extends Element = HTMLDivElement>({
    viewEvent,
    clickEvent,
}: TUsePromoAnalyticsParams) => {
    const { ref, inView } = useInView<T>({ triggerOnce: true })
    const hasSentView = useRef(false)

    useEffect(() => {
        if (inView && !hasSentView.current) {
            hasSentView.current = true
            sendEvent(viewEvent)
        }
    }, [inView, viewEvent])

    const handleClick = () => {
        sendEvent(clickEvent)
    }

    return { ref, handleClick }
}
